import { useEffect, useState } from 'preact/hooks';
import { api } from '../api.ts';
import { changed } from '../signal.ts';
import { showError } from '../toast.ts';
import { fromKg, kcal, today, weight, weightUnit } from '../format.ts';
import type { Insights, Settings } from '../../shared/types.ts';
import { Chart } from './Charts.tsx';

const RANGES = [30, 90, 180] as const;

export function Trends({ settings }: { settings: Settings }) {
  const [days, setDays] = useState<(typeof RANGES)[number]>(30);
  const [data, setData] = useState<Insights | null>(null);
  const tick = changed.use();

  useEffect(() => {
    api<Insights>(`/api/insights?today=${today()}&days=${days}`).then(setData, showError);
  }, [days, tick]);

  if (!data) return null;
  const dates = data.days.map((d) => d.date);
  const recent = data.days.slice(-8, -1).filter((d) => d.intake !== null);
  const avgIntake = recent.length ? recent.reduce((t, d) => t + d.intake!, 0) / recent.length : null;
  const conf = data.confidence;
  const unit = weightUnit(settings);

  return (
    <div class="trends">
      <div class="segmented">
        {RANGES.map((r) => (
          <button key={r} class={days === r ? 'on' : ''} onClick={() => setDays(r)}>{r} days</button>
        ))}
      </div>

      <div class="tiles">
        <div class="card tile">
          <div class="muted">Expenditure</div>
          <div class="big">{kcal(data.expenditure)}</div>
          <div class="muted small">
            kcal/day · {kcal(data.baseline)} baseline + {kcal(data.avgExercise)} exercise
          </div>
        </div>
        <div class="card tile">
          <div class="muted">Trend weight</div>
          <div class="big">{weight(data.trendKg, settings)}</div>
          <div class="muted small">
            {data.weeklyChangeKg === null
              ? 'log weigh-ins to see the trend'
              : `${data.weeklyChangeKg > 0 ? '+' : data.weeklyChangeKg < 0 ? '−' : '±'}${weight(Math.abs(data.weeklyChangeKg), settings, 2)} this week`}
          </div>
        </div>
        <div class="card tile">
          <div class="muted">Avg intake, last 7 days</div>
          <div class="big">{avgIntake === null ? '—' : kcal(avgIntake)}</div>
          <div class="muted small">{recent.length} day{recent.length === 1 ? '' : 's'} logged</div>
        </div>
      </div>

      {conf < 1 && (
        <p class="card note muted">
          {conf === 0
            ? 'The expenditure estimate starts from a formula. After a week of logging food and weighing in, it switches to your own numbers'
            : `The estimate is ${Math.round(conf * 100)}% your own data so far (${data.loggedDays} of 21 logged days)`}
          {' '}and keeps adjusting as your trend weight responds to what you eat.
        </p>
      )}

      <section class="card">
        <h2>Energy</h2>
        <Chart
          label="Daily intake and expenditure"
          dates={dates}
          zeroBased
          series={[
            { key: 'intake', label: 'Intake', kind: 'bar', cls: 's1', values: data.days.map((d) => d.intake), format: kcal },
            { key: 'exp', label: 'Expenditure', kind: 'line', cls: 's2', values: data.days.map((d) => d.expenditure), format: kcal },
          ]}
        />
      </section>

      <section class="card">
        <h2>Weight</h2>
        <Chart
          label="Scale and trend weight"
          dates={dates}
          zeroBased={false}
          series={[
            { key: 'trend', label: 'Trend', kind: 'line', cls: 's1', values: data.days.map((d) => (d.trendKg === null ? null : fromKg(d.trendKg, settings))), format: (v) => `${v.toFixed(1)} ${unit}` },
            { key: 'scale', label: 'Scale', kind: 'dots', cls: 'muted', values: data.days.map((d) => (d.scaleKg === null ? null : fromKg(d.scaleKg, settings))), format: (v) => `${v.toFixed(1)} ${unit}` },
          ]}
        />
      </section>
    </div>
  );
}
